import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip, X, ArrowUp, ArrowDown, ArrowUpDown, Upload } from "lucide-react";
import TransactionImportModal from "@/components/TransactionImportModal";

const CATS = {
  income: ["Satış", "Hizmet", "Kira", "Faiz", "Yatırım", "Diğer"],
  expense: ["Kira", "Maaş", "Fatura", "Vergi", "Yemek", "Ulaşım", "Ofis", "Yazılım", "Pazarlama", "Diğer"],
};

export default function TransactionsPage({ type, title }) {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sort, setSort] = useState({ field: "date", dir: "desc" });
  const [contactFilterId, setContactFilterId] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    const [{ data: tx }, { data: acc }, { data: co }] = await Promise.all([
      api.get(`/transactions?type=${type}`),
      api.get("/accounts"),
      api.get("/contacts"),
    ]);
    setItems(tx); setAccounts(acc); setContacts(co);
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Silindi");
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} kayıt silinsin mi? Bağlı faturalar/çekler otomatik açık hale döner.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/transactions/bulk-delete", { ids });
      toast.success(`${data.deleted} kayıt silindi`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error("Silinemedi: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setBulkDeleting(false);
    }
  };

  const total = items.reduce((s, t) => s + t.amount, 0);

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) {
        // First click default direction: dates descending (newest first), text ascending, amount descending
        const initDir = field === "amount" || field === "date" || field === "due_date" ? "desc" : "asc";
        return { field, dir: initDir };
      }
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const sortedItems = useMemo(() => {
    const accMap = Object.fromEntries(accounts.map((a) => [a.id, a.name]));
    const coMap = Object.fromEntries(contacts.map((c) => [c.id, c.name]));

    // 1) Apply contact filter (exact selection or name text search)
    const q = contactQuery.trim().toLocaleLowerCase("tr");
    const filtered = items.filter((t) => {
      if (contactFilterId) return t.contact_id === contactFilterId;
      if (q) {
        const name = (coMap[t.contact_id] || "").toLocaleLowerCase("tr");
        return name.includes(q);
      }
      return true;
    });

    const getKey = (t) => {
      switch (sort.field) {
        case "due_date": return t.due_date || "";
        case "description": return (t.description || "").toLocaleLowerCase("tr");
        case "category": return (t.category || "").toLocaleLowerCase("tr");
        case "account": return (accMap[t.account_id] || "").toLocaleLowerCase("tr");
        case "contact": return (coMap[t.contact_id] || "").toLocaleLowerCase("tr");
        case "amount": return t.amount;
        default: return t.date || "";
      }
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = getKey(a);
      const bv = getKey(b);
      const aEmpty = av === "" || av === null || av === undefined;
      const bEmpty = bv === "" || bv === null || bv === undefined;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), "tr");
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [items, sort, accounts, contacts, contactFilterId, contactQuery]);

  const isFiltered = contactFilterId || contactQuery.trim();
  const filteredTotal = sortedItems.reduce((s, t) => s + t.amount, 0);
  const visibleIds = useMemo(() => sortedItems.map((t) => t.id), [sortedItems]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const selectedCount = selectedIds.size;
  const selectedTotal = items
    .filter((t) => selectedIds.has(t.id))
    .reduce((s, t) => s + (Number(t.amount) || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">{type === "income" ? "Gelirler & Cari Tahsilatları" : "Giderler & Cari Ödemeleri"}</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">{title}</h1>
          <p className="text-xs text-neutral-500 mt-1.5 max-w-xl">
            {type === "income"
              ? "Cari seçtiğinizde bu işlem müşteriden alınan tahsilat olarak cari kartına ALACAK tarafında düşer, açık faturayı otomatik kapatabilirsiniz."
              : "Cari seçtiğinizde bu işlem tedarikçiye yapılan ödeme olarak cari kartına BORÇ tarafında düşer, açık alış faturasını otomatik kapatabilirsiniz."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={`${type}-import-button`}
            onClick={() => setImportOpen(true)}
            className="brutal-btn bg-white text-black border border-black px-3 py-2.5 text-sm font-medium flex items-center gap-2"
            title={`Excel/CSV ile toplu ${type === "income" ? "tahsilat" : "ödeme"} aktar`}
          >
            <Upload size={16} /> Excel Yükle
          </button>
          <button
            data-testid={`${type}-add-button`}
            onClick={() => { setEditing(null); setOpen(true); }}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} /> Yeni {type === "income" ? "Gelir" : "Gider"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="brutal-card p-5">
          <div className="label-caps">
            {isFiltered ? "Filtrelenmiş Toplam" : "Toplam"}
          </div>
          <div className={`font-mono-num text-3xl mt-2 ${type === "income" ? "text-emerald-700" : "text-rose-600"}`}>
            {fmtTRY(isFiltered ? filteredTotal : total)}
          </div>
        </div>
        <div className="brutal-card p-5">
          <div className="label-caps">Kayıt Sayısı</div>
          <div className="font-mono-num text-3xl mt-2">
            {isFiltered ? sortedItems.length : items.length}
            {isFiltered && (
              <span className="text-sm text-neutral-500 ml-2 font-normal">/ {items.length}</span>
            )}
          </div>
        </div>
        <div className="brutal-card p-5">
          <div className="label-caps">Ortalama</div>
          <div className="font-mono-num text-3xl mt-2">
            {fmtTRY(items.length ? total / items.length : 0)}
          </div>
        </div>
      </div>

      {/* Cari filtresi */}
      <div className="brutal-card p-4 flex flex-wrap items-center gap-3">
        <div className="label-caps">Cari Filtresi</div>
        <input
          data-testid={`${type}-contact-search`}
          placeholder="Cari adına göre ara…"
          value={contactQuery}
          onChange={(e) => { setContactQuery(e.target.value); setContactFilterId(""); }}
          className="flex-1 min-w-[200px] border border-black/20 px-3 py-1.5 outline-none focus:ring-2 focus:ring-black text-sm"
        />
        <select
          data-testid={`${type}-contact-select`}
          value={contactFilterId}
          onChange={(e) => { setContactFilterId(e.target.value); setContactQuery(""); }}
          className="border border-black/20 px-3 py-1.5 outline-none bg-white text-sm min-w-[200px]"
        >
          <option value="">— Cari seç —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {isFiltered && (
          <button
            data-testid={`${type}-contact-clear`}
            onClick={() => { setContactFilterId(""); setContactQuery(""); }}
            className="border border-black/20 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-neutral-100"
          >
            Temizle
          </button>
        )}
      </div>

      <div className="brutal-card overflow-hidden">
        {selectedCount > 0 && (
          <div className="flex items-center justify-between gap-3 border-b-2 border-black bg-neutral-50 px-4 py-2.5"
               data-testid={`${type}-bulk-bar`}>
            <div className="text-sm">
              <b className="font-mono-num" data-testid={`${type}-bulk-count`}>{selectedCount}</b> kayıt seçildi
              <span className="text-neutral-500 mx-2">·</span>
              Toplam:{" "}
              <b className={`font-mono-num ${type === "income" ? "text-emerald-700" : "text-rose-600"}`}>
                {fmtTRY(selectedTotal)}
              </b>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearSelection}
                data-testid={`${type}-bulk-clear`}
                className="border border-black/20 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-white"
              >
                Seçimi Temizle
              </button>
              <button
                type="button"
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid={`${type}-bulk-delete`}
                className="brutal-btn bg-rose-600 text-white px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-40"
              >
                <Trash2 size={12} /> {bulkDeleting ? "Siliniyor…" : "Seçilenleri Sil"}
              </button>
            </div>
          </div>
        )}
        <table className="w-full text-sm" data-testid={`${type}-table`}>
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="pl-4 pr-2 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid={`${type}-select-all`}
                  className="w-4 h-4 accent-rose-600 cursor-pointer"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                  onChange={toggleSelectAllVisible}
                  title={allVisibleSelected ? "Seçimi kaldır" : "Görüneni seç"}
                />
              </th>
              <SortTh field="date" label="Tarih" sort={sort} onToggle={toggleSort} tid={`sort-${type}-date`} />
              <SortTh field="due_date" label="Vade" sort={sort} onToggle={toggleSort} tid={`sort-${type}-due_date`} />
              <SortTh field="description" label="Açıklama" sort={sort} onToggle={toggleSort} tid={`sort-${type}-description`} />
              <SortTh field="category" label="Kategori" sort={sort} onToggle={toggleSort} tid={`sort-${type}-category`} />
              <SortTh field="account" label="Hesap" sort={sort} onToggle={toggleSort} tid={`sort-${type}-account`} />
              <SortTh field="contact" label="Cari" sort={sort} onToggle={toggleSort} tid={`sort-${type}-contact`} />
              <SortTh field="amount" label="Tutar" sort={sort} onToggle={toggleSort} align="right" tid={`sort-${type}-amount`} />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-neutral-500 text-sm">Kayıt yok. Yeni ekleyin.</td></tr>
            )}
            {sortedItems.map((t) => (
              <tr key={t.id}
                  className={`border-b border-black/5 cursor-pointer ${selectedIds.has(t.id) ? "bg-rose-50/50 hover:bg-rose-50" : "hover:bg-neutral-50"}`}
                  onClick={() => { setEditing(t); setOpen(true); }}>
                <td className="pl-4 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    data-testid={`${type}-select-${t.id}`}
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleSelect(t.id)}
                    className="w-4 h-4 accent-rose-600 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 font-mono-num">{fmtDate(t.date)}</td>
                <td className="px-4 py-3 font-mono-num text-neutral-600">{t.due_date ? fmtDate(t.due_date) : "—"}</td>
                <td className="px-4 py-3">{t.description || "—"}</td>
                <td className="px-4 py-3"><span className="border border-black/10 px-2 py-0.5 text-xs">{t.category || "—"}</span></td>
                <td className="px-4 py-3 text-neutral-600">{accounts.find(a => a.id === t.account_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-neutral-600">{contacts.find(c => c.id === t.contact_id)?.name || "—"}</td>
                <td className={`px-4 py-3 text-right font-mono-num font-semibold ${type === "income" ? "text-emerald-700" : "text-rose-600"}`}>
                  {fmtTRY(t.amount)}
                </td>
                <td className="px-4 py-3 text-right">
                  {t.file_id && (
                    <a
                      data-testid={`file-view-${t.id}`}
                      href={`${API}/files/${t.file_id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex mr-2 p-1 hover:bg-neutral-100 border border-transparent hover:border-black/10"
                      title="Fatura / Makbuzu Görüntüle"
                    >
                      <Paperclip size={14} className="text-neutral-500 hover:text-black" />
                    </a>
                  )}
                  <button data-testid={`delete-${type}-${t.id}`} onClick={(e) => { e.stopPropagation(); remove(t.id); }} className="p-1 hover:bg-rose-50">
                    <Trash2 size={14} className="text-rose-600" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <TransactionModal
          type={type}
          editing={editing}
          accounts={accounts}
          contacts={contacts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}

      {importOpen && (
        <TransactionImportModal
          txType={type}
          accounts={accounts}
          onClose={() => setImportOpen(false)}
          onImported={() => { load(); }}
        />
      )}
    </div>
  );
}

function TransactionModal({ type, editing, accounts, contacts, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    type,
    amount: "",
    date: todayISO(),
    due_date: "",
    account_id: accounts[0]?.id || "",
    contact_id: "",
    invoice_id: "",
    category: CATS[type][0],
    description: "",
    file_id: null,
  });
  const [file, setFile] = useState(null);
  const [openInvoices, setOpenInvoices] = useState([]);

  // Cari seçildiğinde açık faturaları getir. income → sales, expense → purchase.
  useEffect(() => {
    if (!form.contact_id || editing) { setOpenInvoices([]); return; }
    const direction = type === "income" ? "sales" : "purchase";
    api.get(`/invoices?contact_id=${form.contact_id}&direction=${direction}`)
      .then(({ data }) => setOpenInvoices(data.filter((i) => i.status === "open")))
      .catch(() => setOpenInvoices([]));
  }, [form.contact_id, type, editing]);

  const selectedInv = openInvoices.find((i) => i.id === form.invoice_id);
  useEffect(() => {
    // Fatura seçildiğinde tutarı ve açıklamayı önerelim.
    if (selectedInv && !editing) {
      setForm((f) => ({
        ...f,
        amount: String(selectedInv.amount),
        description: f.description || `Fatura No: ${selectedInv.invoice_number}`,
        due_date: f.due_date || selectedInv.due_date,
        category: type === "income" ? "Satış Faturası Tahsilatı" : "Alış Faturası Ödemesi",
      }));
    }
  }, [selectedInv, editing, type]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.account_id) { toast.error("Önce bir hesap oluşturun."); return; }
    let file_id = form.file_id;
    if (file) {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      file_id = data.id;
    }
    try {
      // Eğer bir açık fatura seçildiyse → invoice/pay endpoint'i faturayı kapatır + tx oluşturur atomik.
      if (form.invoice_id && !editing) {
        await api.post(`/invoices/${form.invoice_id}/pay`, {
          account_id: form.account_id,
          pay_date: form.date,
        });
      } else {
        const payload = {
          ...form,
          file_id,
          amount: Number(form.amount),
          due_date: form.due_date || null,
        };
        delete payload.invoice_id;
        if (editing) await api.put(`/transactions/${editing.id}`, payload);
        else await api.post("/transactions", payload);
      }
      toast.success(editing ? "Güncellendi" : (form.invoice_id ? "Fatura kapatıldı ve işlem oluşturuldu" : "Eklendi"));
      onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white border border-black w-full max-w-lg" data-testid={`${type}-form-modal`}>
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">
            {editing ? "Düzenle" : `Yeni ${type === "income" ? "Gelir" : "Gider"}`}
          </h2>
          <button type="button" onClick={onClose} className="p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Tutar (₺)">
            <input required type="number" step="0.01" data-testid="tx-amount"
                   value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   className="w-full border border-black/20 px-3 py-2 focus:ring-2 focus:ring-black outline-none font-mono-num text-lg" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tarih (İşlem)">
              <input required type="date" data-testid="tx-date"
                     value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                     className="w-full border border-black/20 px-3 py-2 outline-none" />
            </Field>
            <Field label="Vade Tarihi (opsiyonel)">
              <input type="date" data-testid="tx-due-date"
                     value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     className="w-full border border-black/20 px-3 py-2 outline-none" />
            </Field>
          </div>
          <Field label="Kategori">
            <select data-testid="tx-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-black/20 px-3 py-2 outline-none bg-white">
              {CATS[type].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Hesap">
            <select required data-testid="tx-account" value={form.account_id}
                    onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="w-full border border-black/20 px-3 py-2 outline-none bg-white">
              <option value="">— Seçin —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Cari (opsiyonel)">
            <select data-testid="tx-contact" value={form.contact_id || ""}
                    onChange={(e) => setForm({ ...form, contact_id: e.target.value, invoice_id: "" })}
                    className="w-full border border-black/20 px-3 py-2 outline-none bg-white">
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {form.contact_id && !editing && (
              <div className="text-[11px] text-neutral-500 mt-1.5 border-l-2 border-black pl-2">
                Cari seçildi — bu işlem cari kartında {type === "income" ? "ALACAK (borç kapatma)" : "BORÇ (ödeme)"} olarak kaydedilecek.
              </div>
            )}
          </Field>
          {!editing && form.contact_id && openInvoices.length > 0 && (
            <Field label={`Açık ${type === "income" ? "Satış" : "Alış"} Faturası (opsiyonel — seçilirse fatura kapatılır)`}>
              <select
                data-testid="tx-invoice"
                value={form.invoice_id || ""}
                onChange={(e) => setForm({ ...form, invoice_id: e.target.value })}
                className="w-full border border-black/20 px-3 py-2 outline-none bg-white"
              >
                <option value="">— Bağımsız işlem —</option>
                {openInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.invoice_number} · {fmtDate(i.due_date)} · {fmtTRY(i.amount)}
                  </option>
                ))}
              </select>
              {selectedInv && (
                <div className="text-[11px] text-emerald-700 mt-1.5 border-l-2 border-emerald-600 pl-2">
                  Bu fatura otomatik olarak kapatılacak, tutar ve açıklama önerildi.
                </div>
              )}
            </Field>
          )}
          <Field label="Açıklama">
            <input data-testid="tx-desc" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   className="w-full border border-black/20 px-3 py-2 outline-none" />
          </Field>
          <Field label={`Fatura / Makbuz${form.file_id ? " (yüklü)" : ""}`}>
            <input type="file" data-testid="tx-file" accept="image/*,.pdf"
                   onChange={(e) => setFile(e.target.files?.[0])}
                   className="w-full text-sm file:mr-3 file:border file:border-black file:bg-white file:px-3 file:py-1.5 file:text-xs file:uppercase file:tracking-widest" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid={`${type}-save-button`} type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm font-medium">Kaydet</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-caps mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function SortTh({ field, label, sort, onToggle, align, tid }) {
  const isActive = sort.field === field;
  const Icon = !isActive ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        data-testid={tid}
        onClick={() => onToggle(field)}
        className={`label-caps inline-flex items-center gap-1 select-none ${
          isActive ? "text-black" : "text-neutral-500 hover:text-black"
        } ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        <span>{label}</span>
        <Icon size={11} strokeWidth={2.2} className={isActive ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}
